import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCommunity, commApi, photoUrl } from './CommunityContext';
import { Avatar, AuthDialog, InterestPills } from './CommunityShared';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Anchor,
  Ship,
  MapPin,
  Calendar,
  Users,
  Search,
  MessageCircle,
  LogOut,
  Camera,
  Loader2,
  Compass,
  Sparkles,
  Timer,
  Bell,
  Check,
  X,
  Tag,
} from 'lucide-react';

const HERO = 'https://images.unsplash.com/photo-1559599746-8823b38544c6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';
const CARD_BG = 'https://images.unsplash.com/photo-1580541631950-7282082b53ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=800';
const SUGGESTED = ['Ballo', 'Escursioni', 'Cucina', 'Sport', 'Spa & Relax', 'Musica dal vivo', 'Giochi', 'Fotografia', 'Feste', 'Aperitivi'];

const daysUntil = (d) => {
  if (!d) return null;
  const dep = new Date(d);
  dep.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((dep - today) / 86400000);
};

const countdownLabel = (d) => {
  const days = daysUntil(d);
  if (days === null) return null;
  if (days > 1) return `${days} giorni alla partenza`;
  if (days === 1) return 'Domani si parte!';
  if (days === 0) return 'Si parte oggi! 🎉';
  return 'Viaggio concluso';
};

const CommunityHome = () => {
  const { member, loading, logout } = useCommunity();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [profileOpen, setProfileOpen] = useState(false);
  const [form, setForm] = useState({ ship_name: '', departure_date: '', departure_city: '' });
  const [cruises, setCruises] = useState([]);
  const [myCruises, setMyCruises] = useState([]);
  const [search, setSearch] = useState('');
  const [joining, setJoining] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [promos, setPromos] = useState([]);
  const [featuredIdx, setFeaturedIdx] = useState(0);

  const loadCruises = async (q = '') => {
    try {
      const { data } = await commApi.get('/community/cruises', { params: { search: q } });
      setCruises(data);
    } catch (e) {
      /* public list */
    }
  };

  const loadMyCruises = async () => {
    if (!member) return;
    try {
      const { data } = await commApi.get('/community/my-cruises');
      setMyCruises(data);
    } catch (e) {}
  };

  useEffect(() => {
    loadCruises();
    commApi.get('/community/promotions').then((r) => {
      if (r.data.promotions?.length) setPromos(r.data.promotions);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (promos.length <= 1) return;
    const id = setInterval(() => setFeaturedIdx((i) => (i + 1) % promos.length), 5000);
    return () => clearInterval(id);
  }, [promos]);

  useEffect(() => {
    loadMyCruises();
  }, [member]);

  useEffect(() => {
    if (!member) return;
    const loadUnread = () =>
      commApi.get('/community/messages/unread-count').then((r) => setUnread(r.data.unread)).catch(() => {});
    const loadNotif = () =>
      commApi.get('/community/notifications/unread-count').then((r) => setNotifUnread(r.data.unread)).catch(() => {});
    loadUnread();
    loadNotif();
    const id = setInterval(() => {
      loadUnread();
      loadNotif();
    }, 8000);
    return () => clearInterval(id);
  }, [member]);

  const openNotifications = async () => {
    setNotifOpen(true);
    try {
      const { data } = await commApi.get('/community/notifications');
      setNotifs(data);
      await commApi.post('/community/notifications/mark-read');
      setNotifUnread(0);
    } catch (e) {}
  };

  const openAuth = (mode) => {
    setAuthMode(mode);
    setAuthOpen(true);
  };

  const handleJoin = async () => {
    if (!member) {
      openAuth('register');
      return;
    }
    if (!form.ship_name || !form.departure_date || !form.departure_city) {
      toast.error('Compila nave, data e città di partenza');
      return;
    }
    setJoining(true);
    try {
      const { data } = await commApi.post('/community/cruises/join', form);
      toast.success('Sei a bordo! Ecco la tua tribù 🌊');
      navigate(`/community/cruise/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setJoining(false);
    }
  };

  const joinExisting = async (c) => {
    if (!member) {
      openAuth('register');
      return;
    }
    try {
      await commApi.post('/community/cruises/join', {
        ship_name: c.ship_name,
        departure_date: c.departure_date,
        departure_city: c.departure_city,
      });
      navigate(`/community/cruise/${c.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-slate-900/95 backdrop-blur border-b border-cyan-900/40">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white font-bold text-lg cursor-pointer" onClick={() => navigate('/community')}>
            <Anchor className="w-6 h-6 text-cyan-400" />
            Tribù in Viaggio Community
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              className="text-cyan-100 hover:text-white hover:bg-white/10"
              onClick={() => navigate('/community/promozioni')}
              data-testid="nav-promotions-btn"
            >
              <Tag className="w-4 h-4 mr-1.5" /> Promozioni
            </Button>
            {member ? (
              <>
                <Button
                  variant="ghost"
                  className="text-cyan-100 hover:text-white hover:bg-white/10 relative"
                  onClick={() => navigate('/community/messages')}
                  data-testid="nav-messages-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-1.5" /> Messaggi
                  {unread > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center"
                      data-testid="unread-badge"
                    >
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </Button>
                <button onClick={openNotifications} data-testid="nav-notifications-btn" className="relative text-cyan-100 hover:text-white p-2">
                  <Bell className="w-5 h-5" />
                  {notifUnread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="notif-badge">
                      {notifUnread > 9 ? '9+' : notifUnread}
                    </span>
                  )}
                </button>
                <button onClick={() => setProfileOpen(true)} data-testid="nav-profile-btn">
                  <Avatar member={member} size={38} className="ring-2 ring-cyan-400/60" />
                </button>
                <Button variant="ghost" size="icon" className="text-cyan-100 hover:bg-white/10" onClick={logout} data-testid="nav-logout-btn">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" className="text-cyan-100 hover:text-white hover:bg-white/10" onClick={() => openAuth('login')} data-testid="nav-login-btn">
                  Accedi
                </Button>
                <Button className="bg-cyan-500 hover:bg-cyan-600" onClick={() => openAuth('register')} data-testid="nav-register-btn">
                  Iscriviti
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Crociera" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 py-20 lg:py-28">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/20 text-cyan-200 text-sm font-medium mb-5 border border-cyan-400/30">
              <Sparkles className="w-4 h-4" /> Conosci la tua tribù prima di salpare
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
              Sali a bordo già con
              <span className="text-cyan-400"> amici nuovi</span>
            </h1>
            <p className="mt-5 text-lg text-slate-200 max-w-xl">
              Trova le persone che partono con te — stessa nave, stessa data, stesso porto.
              Chatta, organizza cene, escursioni e feste ancora prima di partire.
            </p>

            {/* Join form */}
            <Card className="mt-8 border-0 shadow-2xl">
              <CardContent className="p-5">
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500 flex items-center gap-1"><Ship className="w-3.5 h-3.5" /> Nave</Label>
                    <Input data-testid="join-ship-input" value={form.ship_name} onChange={(e) => setForm({ ...form, ship_name: e.target.value })} placeholder="Es. MSC Seaview" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Data partenza</Label>
                    <Input data-testid="join-date-input" type="date" value={form.departure_date} onChange={(e) => setForm({ ...form, departure_date: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Città partenza</Label>
                    <Input data-testid="join-city-input" value={form.departure_city} onChange={(e) => setForm({ ...form, departure_city: e.target.value })} placeholder="Es. Genova" />
                  </div>
                </div>
                <Button data-testid="join-submit-btn" onClick={handleJoin} disabled={joining} className="w-full mt-4 h-11 text-base bg-cyan-600 hover:bg-cyan-700">
                  {joining ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Compass className="w-5 h-5 mr-2" />}
                  Trova la mia tribù
                </Button>
                {!member && (
                  <p className="text-xs text-center text-slate-400 mt-2">Per unirti serve l'iscrizione gratuita alla community</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12 space-y-12">
        {/* Featured promotion */}
        {promos.length > 0 && (() => {
          const featured = promos[featuredIdx % promos.length];
          return (
          <section>
            <div
              className="relative rounded-2xl overflow-hidden shadow-xl cursor-pointer group"
              onClick={() => navigate('/community/promozioni')}
              data-testid="featured-promo-banner"
            >
              <div className="absolute inset-0">
                {featured.image_url && <img key={featured.id} src={featured.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                <div className="absolute inset-0 bg-gradient-to-r from-orange-600/90 via-orange-600/70 to-cyan-700/50" />
              </div>
              <div className="relative p-6 sm:p-8 flex items-center justify-between gap-4 flex-wrap">
                <div className="text-white">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/20 border border-white/30 text-xs font-semibold mb-2">
                    <Sparkles className="w-3.5 h-3.5" /> Offerta del momento
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-extrabold">{featured.title}</h3>
                  <div className="flex flex-wrap gap-3 mt-2 text-sm text-orange-50 items-center">
                    {featured.destination && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {featured.destination}</span>}
                    {featured.price && <span className="font-bold">{featured.price}</span>}
                    {(() => {
                      const d = daysUntil(featured.expires_at);
                      if (d === null || d < 0) return null;
                      const lbl = d === 0 ? 'Scade oggi!' : d === 1 ? 'Ultimo giorno!' : `Ancora ${d} giorni!`;
                      return (
                        <span className="inline-flex items-center gap-1 bg-white text-rose-600 text-xs font-bold px-2.5 py-1 rounded-full animate-pulse" data-testid="featured-countdown">
                          <Timer className="w-3.5 h-3.5" /> {lbl}
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <Button className="bg-white text-orange-700 hover:bg-orange-50 font-bold" data-testid="featured-promo-btn">
                  Scopri le offerte →
                </Button>
              </div>
              {promos.length > 1 && (
                <div className="absolute bottom-3 right-4 flex gap-1.5">
                  {promos.map((_, i) => (
                    <button
                      key={i}
                      onClick={(e) => { e.stopPropagation(); setFeaturedIdx(i); }}
                      className={`w-2 h-2 rounded-full transition-all ${i === featuredIdx % promos.length ? 'bg-white w-5' : 'bg-white/50'}`}
                      data-testid={`featured-dot-${i}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
          );
        })()}

        {/* My cruises */}
        {member && myCruises.length > 0 && (
          <section>
            <h2 className="text-lg md:text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Anchor className="w-5 h-5 text-cyan-600" /> Le mie crociere
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {myCruises.map((c) => (
                <CruiseCard key={c.id} c={c} onOpen={() => navigate(`/community/cruise/${c.id}`)} mine />
              ))}
            </div>
          </section>
        )}

        {/* Discover */}
        <section>
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h2 className="text-lg md:text-lg font-bold text-slate-800 flex items-center gap-2">
              <Compass className="w-5 h-5 text-cyan-600" /> Esplora le crociere
            </h2>
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="cruise-search-input"
                className="pl-9"
                placeholder="Cerca per nave o città..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  loadCruises(e.target.value);
                }}
              />
            </div>
          </div>
          {cruises.length === 0 ? (
            <div className="text-center py-14 text-slate-400 bg-white rounded-xl border border-dashed">
              <Ship className="w-10 h-10 mx-auto mb-3 opacity-40" />
              Nessuna crociera ancora. Sii il primo a crearne una qui sopra!
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cruises.map((c) => (
                <CruiseCard key={c.id} c={c} onOpen={() => joinExisting(c)} />
              ))}
            </div>
          )}
        </section>
      </div>

      <AuthDialog open={authOpen} onClose={() => setAuthOpen(false)} defaultMode={authMode} />
      {member && <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />}

      <Dialog open={notifOpen} onOpenChange={setNotifOpen}>
        <DialogContent className="max-w-md" data-testid="notifications-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Bell className="w-5 h-5 text-cyan-600" /> Notifiche</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {notifs.length === 0 ? (
              <p className="text-center text-slate-400 py-8">Nessuna notifica.</p>
            ) : (
              notifs.map((n) => {
                const isEvent = n.type === 'new_event' || n.type === 'new_excursion';
                const isDm = n.type === 'new_dm';
                const clickable = (isEvent && n.group_id) || (isDm && n.from_id);
                const style = n.type === 'approved'
                  ? 'bg-emerald-50 border-emerald-200'
                  : n.type === 'rejected'
                  ? 'bg-rose-50 border-rose-200'
                  : 'bg-cyan-50 border-cyan-200';
                const icon = n.type === 'approved'
                  ? <Check className="w-4 h-4 text-emerald-600" />
                  : n.type === 'rejected'
                  ? <X className="w-4 h-4 text-rose-600" />
                  : isDm
                  ? <MessageCircle className="w-4 h-4 text-cyan-600" />
                  : <Bell className="w-4 h-4 text-cyan-600" />;
                return (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${style} ${clickable ? 'cursor-pointer hover:brightness-95' : ''}`}
                    data-testid={`notif-${n.id}`}
                    onClick={() => {
                      if (isEvent && n.group_id) {
                        setNotifOpen(false);
                        navigate(`/community/cruise/${n.group_id}`);
                      } else if (isDm && n.from_id) {
                        setNotifOpen(false);
                        navigate(`/community/messages/${n.from_id}`);
                      }
                    }}
                  >
                    <div className="mt-0.5">{icon}</div>
                    <div>
                      <p className="text-sm text-slate-700">{n.text}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {n.created_at ? new Date(n.created_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CruiseCard = ({ c, onOpen, mine }) => {
  const dateFmt = c.departure_date
    ? new Date(c.departure_date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';
  return (
    <div
      className="group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 relative"
      onClick={onOpen}
      data-testid={`cruise-card-${c.id}`}
    >
      {/* Ship background */}
      <div className="absolute inset-0">
        <img src={c.image_url || CARD_BG} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-cyan-600/30" />
      </div>

      <div className="relative p-5 min-h-[210px] flex flex-col justify-between text-white">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 bg-white/15 backdrop-blur-md rounded-full pl-2 pr-3 py-1 border border-white/20">
            <Ship className="w-4 h-4 text-cyan-300" />
            <span className="font-bold text-sm">{c.ship_name}</span>
          </div>
          {mine && (
            <span className="text-[11px] bg-emerald-400 text-emerald-950 px-2 py-0.5 rounded-full font-bold shadow">
              Iscritto
            </span>
          )}
        </div>

        <div>
          {mine && countdownLabel(c.departure_date) && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-3 rounded-full bg-orange-400 text-orange-950 text-xs font-bold shadow-lg" data-testid={`countdown-card-${c.id}`}>
              <Timer className="w-3.5 h-3.5" /> {countdownLabel(c.departure_date)}
            </div>
          )}
          <div className="space-y-1 mb-3">
            <p className="text-sm flex items-center gap-1.5 text-cyan-50">
              <MapPin className="w-4 h-4 text-cyan-300" /> {c.departure_city}
            </p>
            <p className="text-sm flex items-center gap-1.5 text-cyan-50">
              <Calendar className="w-4 h-4 text-cyan-300" /> {dateFmt}
            </p>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-white/15">
            <span className="text-sm flex items-center gap-1.5 font-medium">
              <Users className="w-4 h-4 text-cyan-300" /> {c.member_count} {c.member_count === 1 ? 'persona' : 'persone'}
            </span>
            <span className="inline-flex items-center gap-1 bg-cyan-400 text-slate-900 text-sm font-bold px-3 py-1.5 rounded-full group-hover:bg-white transition-colors">
              {mine ? 'Apri' : 'Unisciti'}
              <span className="group-hover:translate-x-0.5 transition-transform">→</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfileDialog = ({ open, onClose }) => {
  const { member, setMember, refreshMe } = useCommunity();
  const [form, setForm] = useState({ name: '', bio: '', interests: [] });
  const [prefs, setPrefs] = useState({ events: true, excursions: true, messages: true });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (member) setForm({ name: member.name || '', bio: member.bio || '', interests: member.interests || [] });
  }, [member, open]);

  useEffect(() => {
    if (open) {
      commApi.get('/community/notification-prefs').then((r) => setPrefs(r.data)).catch(() => {});
      commApi.get('/community/marketing-consent').then((r) => setConsent(r.data.consent)).catch(() => {});
    }
  }, [open]);

  const toggleConsent = async () => {
    const next = !consent;
    setConsent(next);
    try {
      await commApi.put('/community/marketing-consent', { consent: next });
    } catch (e) {
      toast.error('Errore salvataggio consenso');
    }
  };

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      await commApi.put('/community/notification-prefs', next);
    } catch (e) {
      toast.error('Errore salvataggio preferenze');
    }
  };

  const toggleInterest = (i) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i],
    }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await commApi.put('/community/profile', form);
      setMember(data);
      toast.success('Profilo aggiornato');
      onClose();
    } catch (e) {
      toast.error('Errore salvataggio');
    } finally {
      setBusy(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await commApi.post('/community/profile/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await refreshMe();
      toast.success('Foto aggiornata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fallito');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle>Il mio profilo</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Avatar member={member} size={72} />
              <button
                onClick={() => fileRef.current?.click()}
                className="absolute -bottom-1 -right-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-full p-1.5 shadow"
                data-testid="upload-photo-btn"
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPhoto} />
            </div>
            <p className="text-sm text-slate-500">Carica una foto per farti riconoscere dai tuoi compagni di viaggio.</p>
          </div>
          <div>
            <Label>Nome</Label>
            <Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Bio</Label>
            <Textarea data-testid="profile-bio-input" rows={2} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
          </div>
          <div>
            <Label>Interessi</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {SUGGESTED.map((i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleInterest(i)}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    form.interests.includes(i)
                      ? 'bg-cyan-600 text-white border-cyan-600'
                      : 'bg-white text-slate-600 border-slate-300 hover:border-cyan-400'
                  }`}
                >
                  {i}
                </button>
              ))}
            </div>
          </div>
          <div className="border-t pt-3">
            <Label className="flex items-center gap-1.5 mb-2"><Bell className="w-4 h-4 text-cyan-600" /> Preferenze notifiche</Label>
            <div className="space-y-2">
              {[
                { key: 'events', label: 'Nuovi eventi a bordo' },
                { key: 'excursions', label: 'Nuove escursioni' },
                { key: 'messages', label: 'Messaggi privati' },
              ].map((p) => (
                <div key={p.key} className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{p.label}</span>
                  <Switch checked={prefs[p.key]} onCheckedChange={() => togglePref(p.key)} data-testid={`pref-${p.key}`} />
                </div>
              ))}
            </div>
          </div>
          <div className="border-t pt-3">
            <div className="flex items-start justify-between gap-3 rounded-lg bg-orange-50 border border-orange-200 p-3">
              <div>
                <p className="text-sm font-medium text-orange-800 flex items-center gap-1.5"><Tag className="w-4 h-4" /> Nuove offerte</p>
                <p className="text-xs text-orange-700 mt-0.5">Voglio essere ricontattato/a sulle nuove proposte e promozioni di crociera.</p>
              </div>
              <Switch checked={consent} onCheckedChange={toggleConsent} data-testid="marketing-consent-switch" />
            </div>
          </div>
          <Button data-testid="profile-save-btn" onClick={save} disabled={busy} className="w-full bg-cyan-600 hover:bg-cyan-700">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salva
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CommunityHome;
